import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { X, Clock, CheckCircle2, XCircle, CalendarRange } from 'lucide-react';
import FilterCombobox from './FilterCombobox';
import { tasksAPI } from '../lib/api';

const statusOptions = [
  { value: 'null', label: 'Sin Estado', color: 'bg-gray-500', icon: Clock },
  { value: 'estados.en_ejecucion', label: 'En Progreso', color: 'bg-orange-500', icon: Clock },
  { value: 'estados.pendiente', label: 'Pendiente', color: 'bg-gray-400', icon: Clock },
  { value: 'estados.pendiente_entidad_corto', label: 'Pend. Entidad', color: 'bg-gray-400', icon: Clock },
  { value: 'estados.rechazada_corto', label: 'Bloqueada', color: 'bg-pink-600', icon: XCircle },
  { value: 'estados.reparacion.finalizada', label: 'Completada', color: 'bg-[#008040]', icon: CheckCircle2 },
];

const priorityOptions = [
  { value: 5, label: 'Urgente', color: 'bg-red-600' },
  { value: 4, label: 'Alta', color: 'bg-orange-500' },
  { value: 3, label: 'Media', color: 'bg-yellow-500' },
  { value: 2, label: 'Baja', color: 'bg-green-500' },
  { value: 1, label: 'Muy Baja', color: 'bg-blue-500' },
];

export default function FiltersPanel({ filters, onFiltersChange, onClose }) {
  // Local state for date inputs only (avoid API call on every keystroke)
  // Initialized lazily from props; cleared explicitly by clearFilters()
  const [dateFrom, setDateFrom] = useState(() => filters.date_from || '');
  const [dateTo, setDateTo] = useState(() => filters.date_to || '');

  const { data: filterOptions, isLoading: optionsLoading } = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => tasksAPI.getFilterOptions().then(r => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const tipos = filterOptions?.tipos || [];
  const entidades = filterOptions?.entidades || [];
  const distritos = filterOptions?.distritos || [];

  // Immediate toggle for status — applies directly
  const toggleStatus = (value) => {
    const cur = filters.status || [];
    onFiltersChange({
      ...filters,
      status: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value],
    });
  };

  // Immediate toggle for priority — applies directly
  const togglePriority = (value) => {
    const cur = filters.priority || [];
    onFiltersChange({
      ...filters,
      priority: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value],
    });
  };

  // Immediate change for combobox fields
  const handleComboChange = (field, value) => {
    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  // Apply date fields on blur / when user finishes typing
  const applyDates = () => {
    onFiltersChange({
      ...filters,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    onFiltersChange({});
  };

  const hasActiveFilters =
    (filters.status?.length > 0) ||
    (filters.priority?.length > 0) ||
    filters.tipo ||
    filters.entidad ||
    filters.distrito ||
    filters.date_from ||
    filters.date_to;

  const activeCount = (filters.status?.length || 0) +
    (filters.priority?.length || 0) +
    (filters.tipo ? 1 : 0) +
    (filters.entidad ? 1 : 0) +
    (filters.distrito ? 1 : 0) +
    ((filters.date_from || filters.date_to) ? 1 : 0);

  return (
    <div className="p-4 border-b border-border bg-muted/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm text-foreground">Filtros</h3>
          {hasActiveFilters && (
            <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasActiveFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="clear-filters"
              className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground">
              Limpiar todo
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Estado */}
      <div className="mb-4">
        <Label className="text-xs text-muted-foreground mb-2 block font-medium">Estado</Label>
        <div className="grid grid-cols-2 gap-1.5">
          {statusOptions.map((opt) => {
            const Icon = opt.icon;
            const sel = (filters.status || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => toggleStatus(opt.value)}
                data-testid={`filter-status-${opt.value}`}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  sel
                    ? `${opt.color} text-white border-transparent shadow-sm`
                    : 'bg-white dark:bg-gray-800 text-foreground border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <Icon className={`w-3 h-3 flex-shrink-0 ${sel ? 'text-white' : 'text-muted-foreground'}`} />
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Prioridad */}
      <div className="mb-4">
        <Label className="text-xs text-muted-foreground mb-2 block font-medium">Prioridad</Label>
        <div className="flex flex-wrap gap-1.5">
          {priorityOptions.map((opt) => {
            const sel = (filters.priority || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => togglePriority(opt.value)}
                data-testid={`filter-priority-${opt.value}`}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  sel
                    ? `${opt.color} text-white border-transparent shadow-sm`
                    : 'bg-white dark:bg-gray-800 text-foreground border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sel ? 'bg-white' : opt.color}`} />
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Advanced filters divider */}
      <div className="border-t border-border/60 pt-3 mb-3">
        <Label className="text-xs text-muted-foreground font-medium">Filtros Avanzados</Label>
      </div>

      {/* Tipo de Incidencia */}
      <div className="mb-2.5">
        <Label className="text-[11px] text-muted-foreground mb-1 block">Tipo de Incidencia</Label>
        <FilterCombobox
          label="tipo"
          value={filters.tipo || ''}
          options={tipos}
          onChange={v => handleComboChange('tipo', v)}
          loading={optionsLoading}
          placeholder="Todos los tipos..."
        />
      </div>

      {/* Entidad Reparadora */}
      <div className="mb-2.5">
        <Label className="text-[11px] text-muted-foreground mb-1 block">Entidad Reparadora</Label>
        <FilterCombobox
          label="entidad"
          value={filters.entidad || ''}
          options={entidades}
          onChange={v => handleComboChange('entidad', v)}
          loading={optionsLoading}
          placeholder="Todas las entidades..."
        />
      </div>

      {/* Distrito */}
      <div className="mb-2.5">
        <Label className="text-[11px] text-muted-foreground mb-1 block">Distrito</Label>
        <FilterCombobox
          label="distrito"
          value={filters.distrito || ''}
          options={distritos}
          onChange={v => handleComboChange('distrito', v)}
          loading={optionsLoading}
          placeholder="Todos los distritos..."
        />
      </div>

      {/* Date Range Filter */}
      <div className="border-t border-border/60 pt-3 mb-3">
        <Label className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
          <CalendarRange className="w-3.5 h-3.5" /> Rango de Fecha (Comunicación)
        </Label>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-[11px] text-muted-foreground mb-1 block">Desde</Label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            onBlur={applyDates}
            data-testid="filter-date-from"
            className="w-full px-2 py-1.5 rounded-lg text-xs text-foreground border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground mb-1 block">Hasta</Label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            onBlur={applyDates}
            data-testid="filter-date-to"
            className="w-full px-2 py-1.5 rounded-lg text-xs text-foreground border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      {(dateFrom || dateTo) && (
        <Button size="sm" onClick={applyDates}
          className="w-full mb-2 h-7 text-xs bg-[#008040] hover:bg-[#006633]"
          data-testid="apply-date-filter">
          Aplicar fechas
        </Button>
      )}

      {/* Active filters summary */}
      {hasActiveFilters && (
        <div className="mt-2 p-2 bg-primary/10 rounded-lg text-xs space-y-0.5 text-foreground">
          {filters.status?.length > 0 && <div><span className="font-medium">Estado:</span> {filters.status.length} seleccionado(s)</div>}
          {filters.priority?.length > 0 && <div><span className="font-medium">Prioridad:</span> {filters.priority.length} seleccionada(s)</div>}
          {filters.tipo && <div><span className="font-medium">Tipo:</span> {filters.tipo.length > 28 ? filters.tipo.substring(0, 28) + '…' : filters.tipo}</div>}
          {filters.entidad && <div><span className="font-medium">Entidad:</span> {filters.entidad}</div>}
          {filters.distrito && <div><span className="font-medium">Distrito:</span> {filters.distrito}</div>}
          {(filters.date_from || filters.date_to) && (
            <div><span className="font-medium">Fecha:</span> {filters.date_from || '…'} → {filters.date_to || '…'}</div>
          )}
        </div>
      )}
    </div>
  );
}
